/**
 * Utility class for React Native WebView integration.
 *
 * This class uses Window interface to include ReactNativeWebView,
 * which is injected by React Native WebView components to enable communication
 * between the web app and the native mobile app.
 *
 * Environment detection:
 * - Regular browsers: window.ReactNativeWebView doesn't exist (false)
 * - React Native WebView: window.ReactNativeWebView exists with postMessage method
 *
 * Usage:
 * ```typescript
 * import { ReactNativeUtils } from './ReactNativeUtils';
 *
 * if (ReactNativeUtils.isReactNative()) {
 *   // React Native specific code
 *   window.ReactNativeWebView.postMessage('Hello Native!');
 * } else {
 *   // Browser specific code
 * }
 * ```
 */

/**
 * Interface for React Native WebView bridge object.
 * Provides postMessage method for communication with native app.
 */
export interface IReactNativeWebView {
    postMessage: (message: string) => void;
}

/**
 * Uses Window interface that includes React Native WebView bridge.
 * This global declaration makes ReactNativeWebView available on window object.
 */
declare global {
    interface Window {
        ReactNativeWebView?: IReactNativeWebView;
    }
}

declare const window: Window;

/**
 * Utility class for React Native environment detection.
 */
export class ReactNativeUtils {
    /**
     * Detects if the current environment is a React Native WebView.
     *
     * @returns {boolean} true if running in React Native WebView, false otherwise
     */
    public static isReactNative(): boolean {
        return !!window.ReactNativeWebView;
    }
}
